// PWA Utilities for Family Archive
// Handles service worker registration and install prompts

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export class PWAManager {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private isInstalled = false;
  private isStandalone = false;

  constructor() {
    this.init();
  }

  private init() {
    // Check if app is running in standalone mode
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                       (window.navigator as any).standalone ||
                       document.referrer.includes('android-app://');

    // Check if app is already installed
    this.isInstalled = this.isStandalone;

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      this.showInstallButton();
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.hideInstallButton();
      this.showInstallSuccess();
    });

    // Register service worker
    this.registerServiceWorker();
  }

  private async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered successfully:', registration);

        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                this.showUpdateAvailable();
              }
            });
          }
        });

        // Request notification permission
        if ('Notification' in window && Notification.permission === 'default') {
          await Notification.requestPermission();
        }

      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  }

  public async showInstallPrompt(): Promise<boolean> {
    if (!this.deferredPrompt) {
      this.showManualInstallInstructions();
      return false;
    }

    try {
      await this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        return true;
      } else {
        console.log('User dismissed the install prompt');
        return false;
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    } finally {
      this.deferredPrompt = null;
    }
  }

  public canInstall(): boolean {
    return !this.isInstalled && (this.deferredPrompt !== null || this.isIOS());
  }

  public isAppInstalled(): boolean {
    return this.isInstalled;
  }

  public isRunningStandalone(): boolean {
    return this.isStandalone;
  }

  private isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  private isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  private showInstallButton() {
    // Dispatch custom event to show install button
    window.dispatchEvent(new CustomEvent('pwa-install-available'));
  }

  private hideInstallButton() {
    // Dispatch custom event to hide install button
    window.dispatchEvent(new CustomEvent('pwa-install-completed'));
  }

  private showInstallSuccess() {
    // Show success message
    window.dispatchEvent(new CustomEvent('pwa-install-success', {
      detail: { message: 'Family Archive installed successfully!' }
    }));
  }

  private showUpdateAvailable() {
    // Show update available message
    window.dispatchEvent(new CustomEvent('pwa-update-available', {
      detail: { message: 'A new version is available. Refresh to update.' }
    }));
  }

  private showManualInstallInstructions() {
    const isIOS = this.isIOS();
    const isSafari = this.isSafari();

    let instructions = '';
    
    if (isIOS && isSafari) {
      instructions = `To install Family Archive on your iPhone:
1. Tap the Share button (square with arrow) at the bottom
2. Scroll down and tap "Add to Home Screen"
3. Tap "Add" to install the app`;
    } else if (isIOS) {
      instructions = `To install Family Archive:
1. Open this page in Safari
2. Tap the Share button and select "Add to Home Screen"`;
    } else {
      instructions = `To install Family Archive:
1. Open the browser menu (⋮)
2. Select "Add to Home screen" or "Install app"`;
    }

    window.dispatchEvent(new CustomEvent('pwa-show-instructions', {
      detail: { instructions, isIOS, isSafari }
    }));
  }

  public async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  public showNotification(title: string, options?: NotificationOptions) {
    if (Notification.permission === 'granted') {
      new Notification(title, {
        icon: '/logo192.png',
        badge: '/favicon.ico',
        ...options
      });
    }
  }

  public async forceUpdate(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
          window.location.reload();
        }
      } catch (error) {
        console.error('Force update failed:', error);
        // Fallback: just reload the page
        window.location.reload();
      }
    } else {
      // Fallback: just reload the page
      window.location.reload();
    }
  }
}

// Create singleton instance
export const pwaManager = new PWAManager();
